import * as Sentry from '@sentry/react';
import { isAxiosError } from 'axios';
import { useEffect, useRef } from 'react';
import { useConfigurationStorage } from '@data';
import {
  getExtensionOpenedAttributes,
  maskMailboxInUrl,
  OFFICE_STRINGS_FAILED_MESSAGE,
  setSentryContext,
} from '@utils-global';

// Sentry DSN is commented out in .env.development; uncomment and restart dev server
// for local testing.
export const initSentry = () => {
  Sentry.init({
    dsn: process.env.SENTRY_DSN,
    enableLogs: true,
    // Counters for expected, unactionable outcomes we still want a trend for, e.g. a mailbox
    // with no spam folder: counted here rather than raised as an error to triage.
    enableMetrics: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      // A console breadcrumb carries whatever was logged (a mail's MIME, a request body) on
      // every later error event. Context for Sentry goes through Sentry.addBreadcrumb instead.
      Sentry.breadcrumbsIntegration({ console: false }),
    ],
    environment: process.env.TARGET_ENV || 'production',
    release: process.env.RELEASE_VERSION,
    // Already reported by the Office.onReady watchdog (office.unavailable_cause tag).
    ignoreErrors: [OFFICE_STRINGS_FAILED_MESSAGE],
    // Request breadcrumbs (xhr, fetch, graph-api) record Graph URLs, which name the mailbox.
    beforeBreadcrumb: (breadcrumb) => {
      const url = breadcrumb.data?.url;
      if (typeof url !== 'string') {
        return breadcrumb;
      }
      return { ...breadcrumb, data: { ...breadcrumb.data, url: maskMailboxInUrl(url) } };
    },
    // Discard transient client-side network failures (XHR status 0: user offline, the add-in
    // webview torn down mid-request). Axios reports these as ERR_NETWORK, they are not
    // actionable application bugs and only add noise to Sentry. Network errors that warrant
    // handling should be retried at the corresponding call site.
    beforeSend: (event, hint) => {
      const error = hint?.originalException;
      if (isAxiosError(error) && error.code === 'ERR_NETWORK') {
        return null;
      }
      return event;
    },
  });
};

export const useSentryInit = () => {
  const { configuration } = useConfigurationStorage();
  const hasCountedOpen = useRef(false);

  useEffect(() => {
    setSentryContext({ config: configuration?.data });
  }, [configuration]);

  useEffect(() => {
    if (hasCountedOpen.current || !configuration?.data) {
      return;
    }
    hasCountedOpen.current = true;

    Sentry.metrics.count('extension.opened', 1, {
      attributes: getExtensionOpenedAttributes(configuration.data),
    });
  }, [configuration]);
};
